import { CellStates } from '@components/CustomAllocationEditor/cellStates';
import type { useCustomAllocationDraft } from '@components/CustomAllocationEditor/useCustomAllocationDraft';
import type { PortfolioAllocationGridSlots } from '@components/PortfolioAllocationGrid';
import {
  TableCell,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import {
  type CellStateTones,
  CustomAllocationCell,
} from '@ovComponents/4-module/widgets/portfolio/components/customAllocationCell';
import type {
  PortfolioSecurity,
  PortfolioSubAccount,
} from '@ovComponents/4-module/widgets/portfolio/components/interfaces';
import { isTradeableAllocationMember } from '@ovComponents/4-module/widgets/portfolio/components/tradeableAllocationMembers';
import {
  isCashSecurity,
  PORTFOLIO_CELL_HEIGHT,
  PORTFOLIO_COLUMN_WIDTH,
} from '@ovComponents/4-module/widgets/portfolio/components/utils';
import { AllocationUnits } from '@utils/allocationUnits';
import { formatPercentValue } from '@utils/moneyFormatting';
import { Box, IconButton, Typography } from 'hephaestus/web';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from 'hephaestus/webIcons';
import type { TFunction } from 'i18next';
import { ACCOUNT_ALLOCATION_HEAD_HEIGHT } from './AccountAllocationLeftHead';
import {
  type AccountColumnLabel,
  accountColumnLabel,
  accountColumnLabelLines,
} from './accountColumnLabel';
import { cellFraction } from './placeholderMatrix';
import type {
  AccountAllocationGridAccount,
  AccountAllocationGridReport,
} from './types';

const EN_DASH = '–';
const CELL_SX = { height: PORTFOLIO_CELL_HEIGHT.standard };
// The left and right tables only line up when every row has the same height,
// so input cells take the text cells' height instead of shrinking to the input.
const INPUT_CELL_SX = { ...CELL_SX, textAlign: 'right' } as const;

export const TESTID_ACCOUNT_ALLOCATION_CELL = (
  subAccountId: string,
  financialProductId: string,
) => `account-allocation-cell-${subAccountId}-${financialProductId}`;
export const TESTID_ACCOUNT_ALLOCATION_CASH_CELL = (subAccountId: string) =>
  `account-allocation-cash-${subAccountId}`;
export const TESTID_ACCOUNT_ALLOCATION_TOTAL_CELL = (subAccountId: string) =>
  `account-allocation-total-${subAccountId}`;
export const TESTID_ACCOUNT_ALLOCATION_TARGET_CELL = (
  financialProductId: string,
) => `account-allocation-target-${financialProductId}`;

export const TESTID_ACCOUNT_ALLOCATION_TARGET_TOTAL_CELL =
  'account-allocation-target-total';

// Cells only ever hold a percentage, so a column needs far less room than the
// Portfolio tab's, whose cells hold editable amounts.
const ACCOUNT_COLUMN_MIN_PX = 104;
const ACCOUNT_COLUMN_MAX_PX = 160;
const HEADER_CHAR_PX = 8;

const accountColumnWidthPx = (lines: string[]) =>
  Math.min(
    Math.max(
      ...lines.map((line) => line.length * HEADER_CHAR_PX),
      ACCOUNT_COLUMN_MIN_PX,
    ),
    ACCOUNT_COLUMN_MAX_PX,
  );

// Only the pinned cash row is dropped: household cash excludes cash-equivalent
// funds, so they need rows of their own for the totals to reach 100%.

// Drops cash however it is flagged, matching the staged matrix: a row it shows
// is a cell the matrix can stage, so no lock can name a cell staging rejects.
export const accountAllocationRows = (securities: PortfolioSecurity[]) =>
  securities.filter((security) => !isCashSecurity(security));

export type BuildAccountAllocationSlotsArgs = {
  report: AccountAllocationGridReport;
  securities: PortfolioSecurity[];
  isAssetClassPortfolio: boolean;
  readOnly: boolean;
  draft: ReturnType<typeof useCustomAllocationDraft>;
  stateTones: CellStateTones;
  onCommitCell: (
    subAccountId: string,
    financialProductId: string,
    fraction: number,
  ) => void;
  t: TFunction;
};

export const buildAccountAllocationSlots = ({
  report,
  securities,
  isAssetClassPortfolio,
  readOnly,
  draft,
  stateTones,
  onCommitCell,
  t,
}: BuildAccountAllocationSlotsArgs): PortfolioAllocationGridSlots => {
  const accountsBySubAccountId = new Map(
    report.subAccounts.map((account) => [account.subAccount.id, account]),
  );

  const accountNumberOf = (subAccount: PortfolioSubAccount) => {
    const account = accountsBySubAccountId.get(subAccount.id)?.subAccount
      .account;
    return account
      ? accountColumnLabel(account, t).accountNumber
      : subAccount.name;
  };

  // Every held cell reads through the draft so totals follow edits; an
  // account the draft cannot edit just falls back to its stored value.
  const accountFraction = (
    account: AccountAllocationGridAccount | undefined,
    security: PortfolioSecurity,
  ) =>
    account &&
    cellFraction(
      account,
      {
        expectedPercentage: security.expectedPercentage,
        financialProduct: { id: security.id },
      },
      isAssetClassPortfolio,
    ) !== undefined
      ? draft.displayFraction(
          account.subAccount.id,
          security.id,
          security.expectedPercentage,
        )
      : undefined;

  const householdTotal = securities.reduce(
    (total, security) => total + security.expectedPercentage,
    report.expectedCashPercentage,
  );
  const accountTotal = (account: AccountAllocationGridAccount | undefined) =>
    securities.reduce(
      (total, security) => total + (accountFraction(account, security) ?? 0),
      report.expectedCashPercentage,
    );

  const assetClassSpacerCells = isAssetClassPortfolio && (
    <>
      <TableCell
        sx={{ ...CELL_SX, width: PORTFOLIO_COLUMN_WIDTH.expand, padding: 0 }}
      />
      <TableCell sx={CELL_SX} />
    </>
  );

  const leftRow = (label: string, fraction: number, testId?: string) => (
    <TableRow>
      {assetClassSpacerCells}
      <TableCell sx={CELL_SX}>{label}</TableCell>
      <TableCell sx={CELL_SX} right data-testid={testId}>
        {formatPercentValue(fraction)}
      </TableCell>
    </TableRow>
  );

  return {
    cashRow: () =>
      leftRow(
        t('components:portfolioTable.cash'),
        report.expectedCashPercentage,
      ),
    securityRow: (security) =>
      leftRow(
        security.ticker || security.translatedName,
        security.expectedPercentage,
        TESTID_ACCOUNT_ALLOCATION_TARGET_CELL(security.id),
      ),
    assetClassGroupRow: (
      group,
      { isExpanded, onToggleExpand, renderSecurityRows },
    ) => (
      <>
        <TableRow>
          <TableCell
            sx={{
              ...CELL_SX,
              width: PORTFOLIO_COLUMN_WIDTH.expand,
              padding: 0,
            }}
          >
            <IconButton
              aria-label={
                isExpanded
                  ? t('components:portfolioTable.collapseRow')
                  : t('components:portfolioTable.expandRow')
              }
              aria-expanded={isExpanded}
              size='xsmall'
              onClick={onToggleExpand}
              sx={{ padding: 0 }}
            >
              {isExpanded ? (
                <KeyboardArrowUpIcon fontSize='inherit' />
              ) : (
                <KeyboardArrowDownIcon fontSize='inherit' />
              )}
            </IconButton>
          </TableCell>
          <TableCell sx={CELL_SX}>
            <Typography
              variant='bodySmall'
              component='p'
              sx={{ fontWeight: 'bold' }}
            >
              {group.name}
            </Typography>
          </TableCell>
          <TableCell sx={CELL_SX} />
          <TableCell sx={CELL_SX} right>
            {formatPercentValue(group.groupPct)}
          </TableCell>
        </TableRow>
        {isExpanded && renderSecurityRows()}
      </>
    ),
    securityTotalRow: () =>
      leftRow(
        t('components:portfolioTable.total'),
        householdTotal,
        TESTID_ACCOUNT_ALLOCATION_TARGET_TOTAL_CELL,
      ),
    subAccountHeadCell: (subAccount) => {
      const account = accountsBySubAccountId.get(subAccount.id)?.subAccount
        .account;
      const label: AccountColumnLabel = account
        ? accountColumnLabel(account, t)
        : { accountNumber: subAccount.name };
      const { ownerLine, accountType, accountNumber } = label;
      const readOnlyLabel = isTradeableAllocationMember(subAccount)
        ? undefined
        : t('components:accountAllocationGrid.readOnlyAccount');
      const lines = accountColumnLabelLines(label);
      const widthPx = accountColumnWidthPx(
        readOnlyLabel ? [...lines, readOnlyLabel] : lines,
      );
      const lineSx = {
        display: 'block',
        maxWidth: `${widthPx}px`,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      };

      return (
        <TableHeadCell
          key={subAccount.id}
          title={lines.join(' ')}
          sx={{
            width: `${widthPx}px`,
            minWidth: `${widthPx}px`,
            height: ACCOUNT_ALLOCATION_HEAD_HEIGHT,
          }}
          number
        >
          {ownerLine && (
            <>
              <Box component='span' sx={lineSx}>
                {ownerLine}
              </Box>{' '}
            </>
          )}
          {accountType && (
            <>
              <Box component='span' sx={lineSx}>
                {accountType}
              </Box>{' '}
            </>
          )}
          <Box component='span' sx={lineSx}>
            {accountNumber}
          </Box>
          {readOnlyLabel && (
            <>
              {' '}
              <Typography
                component='span'
                variant='bodySmall'
                sx={{ ...lineSx, color: 'inherit', opacity: 0.8 }}
              >
                {readOnlyLabel}
              </Typography>
            </>
          )}
        </TableHeadCell>
      );
    },
    subAccountSummaryCell: (subAccount) => (
      <TableCell
        key={subAccount.id}
        sx={CELL_SX}
        right
        data-testid={TESTID_ACCOUNT_ALLOCATION_CASH_CELL(subAccount.id)}
      >
        {formatPercentValue(report.expectedCashPercentage)}
      </TableCell>
    ),
    holdingCell: (security, subAccount) => {
      const fraction = accountFraction(
        accountsBySubAccountId.get(subAccount.id),
        security,
      );
      const testId = TESTID_ACCOUNT_ALLOCATION_CELL(subAccount.id, security.id);

      if (
        readOnly ||
        fraction === undefined ||
        !isTradeableAllocationMember(subAccount)
      ) {
        return (
          <TableCell sx={CELL_SX} right data-testid={testId}>
            {fraction === undefined ? EN_DASH : formatPercentValue(fraction)}
          </TableCell>
        );
      }

      const cellState = draft.cellState(subAccount.id, security.id);
      const accountNumber = accountNumberOf(subAccount);
      const labels = {
        security: security.ticker || security.translatedName,
        member: accountNumber,
        account: accountNumber,
      };
      return (
        <CustomAllocationCell
          unit={AllocationUnits.PERCENT}
          fraction={fraction}
          mvCents={subAccount.marketValueCents}
          isLocked={cellState !== CellStates.DERIVED}
          uploaded={cellState === CellStates.UPLOADED}
          ariaLabel={
            t('components:householdPortfolio.customTargetInputLabel', labels) ??
            undefined
          }
          releaseLabel={
            t('components:accountAllocationGrid.releaseCell', labels) ??
            undefined
          }
          testId={testId}
          sx={INPUT_CELL_SX}
          stateTones={stateTones}
          onCommit={(nextFraction) =>
            onCommitCell(subAccount.id, security.id, nextFraction)
          }
          onRelease={() => draft.releaseCell(subAccount.id, security.id)}
        />
      );
    },
    subAccountTotalCell: (subAccount) => (
      <TableCell
        key={subAccount.id}
        sx={CELL_SX}
        right
        data-testid={TESTID_ACCOUNT_ALLOCATION_TOTAL_CELL(subAccount.id)}
      >
        {formatPercentValue(
          accountTotal(accountsBySubAccountId.get(subAccount.id)),
        )}
      </TableCell>
    ),
  };
};
